import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * SAB-459: the persistent NFL draft queue.
 *
 * Replaces the per-session `useState<string[]>` that died on refresh and was invisible to
 * nfl-auto-pick. Rows live in nfl_draft_queues, owner-only by RLS.
 *
 * ── OPTIMISTIC WITH ROLLBACK ────────────────────────────────────────────────
 *
 * Every mutation applies to local state first, then writes. On failure the PREVIOUS order
 * is restored and an onError callback fires. That matters more here than in most places: a
 * queue is what the server picks from when your clock runs out, so a UI that shows a player
 * queued when the write failed is telling you that you are covered when you are not.
 *
 * ── PRIORITY IS DENSE AND REWRITTEN WHOLE ───────────────────────────────────
 *
 * Reordering rewrites every row's priority as its 0-based index rather than juggling
 * sparse values. It costs one upsert of N rows instead of a clever two-row swap, and in
 * exchange the invariant "priority == position in the list" is always true, so a partially
 * applied reorder cannot leave two players claiming the same slot.
 */

export interface NFLQueueEntry {
  player_id: string;
  priority: number;
}

interface UseNFLDraftQueueArgs {
  leagueId: string | null;
  teamId: string | null;
  /** Surfaced to the caller so a failed write can be toasted rather than swallowed. */
  onError?: (message: string) => void;
}

export function useNFLDraftQueue({ leagueId, teamId, onError }: UseNFLDraftQueueArgs) {
  const [queue, setQueue] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const fail = useCallback((msg: string) => {
    console.error('[draft-queue]', msg);
    onErrorRef.current?.(msg);
  }, []);

  // Restore on mount / whenever the team changes. This is the whole point of the ticket:
  // the queue must survive a refresh.
  const load = useCallback(async () => {
    if (!leagueId || !teamId) { setQueue([]); return; }
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('nfl_draft_queues')
      .select('player_id, priority')
      .eq('league_id', leagueId)
      .eq('team_id', teamId)
      .order('priority', { ascending: true });
    setLoading(false);
    if (error) { fail(`load failed: ${error.message}`); return; }
    setQueue(((data ?? []) as NFLQueueEntry[]).map(r => r.player_id));
  }, [leagueId, teamId, fail]);

  useEffect(() => { load(); }, [load]);

  /** Rewrite the whole queue's priorities to match `next`, then reconcile. */
  const persist = useCallback(async (next: string[], previous: string[]) => {
    if (!leagueId || !teamId) return;
    setSaving(true);
    try {
      // Remove anything no longer queued, then upsert the rest with dense priorities.
      const removed = previous.filter(id => !next.includes(id));
      if (removed.length > 0) {
        const { error } = await (supabase as any)
          .from('nfl_draft_queues')
          .delete()
          .eq('team_id', teamId)
          .in('player_id', removed);
        if (error) throw new Error(error.message);
      }
      if (next.length > 0) {
        const rows = next.map((player_id, i) => ({
          league_id: leagueId, team_id: teamId, player_id, priority: i,
        }));
        const { error } = await (supabase as any)
          .from('nfl_draft_queues')
          .upsert(rows, { onConflict: 'team_id,player_id' });
        if (error) throw new Error(error.message);
      }
    } catch (e) {
      // ROLLBACK. Restoring the previous order is the honest thing to show: the server
      // still holds `previous`, so leaving `next` on screen would misrepresent what the
      // auto-pick will actually do.
      setQueue(previous);
      fail(`save failed, queue restored: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }, [leagueId, teamId, fail]);

  const toggle = useCallback((playerId: string) => {
    setQueue(prev => {
      const next = prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId];
      void persist(next, prev);
      return next;
    });
  }, [persist]);

  /** Move an entry to a new index. Used by both drag-reorder and the move controls. */
  const reorder = useCallback((from: number, to: number) => {
    setQueue(prev => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      void persist(next, prev);
      return next;
    });
  }, [persist]);

  const remove = useCallback((playerId: string) => {
    setQueue(prev => {
      if (!prev.includes(playerId)) return prev;
      const next = prev.filter(id => id !== playerId);
      void persist(next, prev);
      return next;
    });
  }, [persist]);

  return { queue, loading, saving, toggle, reorder, remove, reload: load };
}
